import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationLabels } from '@repo/ui-contracts';
import { cn } from '../lib/utils';

type PaginationProps = React.ComponentPropsWithoutRef<'nav'> & {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  labels: PaginationLabels;
};

function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | 'ellipsis')[] = [1];
  if (current > 3) pages.push('ellipsis');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('ellipsis');
  pages.push(total);
  return pages;
}

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  ({ currentPage, totalPages, onPageChange, labels, className, ...props }, ref) => {
    const pages = getPageNumbers(currentPage, totalPages);

    return (
      <nav ref={ref} aria-label="Pagination" className={cn('', className)} {...props}>
        <ul className="flex items-center gap-1">
          <li>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="inline-flex h-control-base w-control-base items-center justify-center rounded-md text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-disabled"
              aria-label={labels.previous}
            >
              <ChevronLeft className="size-icon-sm" />
            </button>
          </li>
          {pages.map((page, index) =>
            page === 'ellipsis' ? (
              <li key={`ellipsis-${index}`}>
                <span className="inline-flex h-control-base w-control-base items-center justify-center text-sm text-muted-foreground">
                  {labels.ellipsis}
                </span>
              </li>
            ) : (
              <li key={page}>
                <button
                  type="button"
                  onClick={() => onPageChange(page)}
                  aria-label={labels.page(page)}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={cn(
                    'inline-flex h-control-base w-control-base items-center justify-center rounded-md text-sm transition-colors',
                    page === currentPage
                      ? 'bg-primary font-medium text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {page}
                </button>
              </li>
            ),
          )}
          <li>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="inline-flex h-control-base w-control-base items-center justify-center rounded-md text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-disabled"
              aria-label={labels.next}
            >
              <ChevronRight className="size-icon-sm" />
            </button>
          </li>
        </ul>
      </nav>
    );
  },
);
Pagination.displayName = 'Pagination';

export { Pagination };
export type { PaginationProps };
